import { DateInput } from "@msuite/ui/date-input";
import { FormSwitch } from "@msuite/ui/form";
import { Icon } from "@msuite/ui/icons";
import { resolver } from "@msuite/ui/libs/form";
import { useTranslation } from "@msuite/ui/localization";
import { type AsyncModalController, ConfirmModal } from "@msuite/ui/modal";
import { moment } from "@msuite/utils/date";
import {
	type SubstitutionPlanSubmitQuerySchema,
	substitutionPlanSubmitQuerySchema,
} from "@msuite/utils/schemas";
import { useForm, useWatch } from "react-hook-form";

/** Props Interface */
interface SubstitutionPlanSubmitModalProps {
	controller: AsyncModalController<SubstitutionPlanSubmitQuerySchema>;
	startDate: string;
}

export function SubstitutionPlanSubmitModal({
	controller,
	startDate,
}: SubstitutionPlanSubmitModalProps) {
	/** Hooks */
	const { t } = useTranslation();

	/** Form */
	const methods = useForm({
		resolver: resolver(substitutionPlanSubmitQuerySchema),
		defaultValues: {
			submit: true,
			schedule: false,
			sendEmailsScheduleDate: startDate,
		},
	});
	const schedule = useWatch({ control: methods.control, name: "schedule" }) as boolean;

	/** Functions */
	async function handleOnSubmit() {
		const values = methods.getValues();
		values.submit = true;
		return values;
	}

	/** Render */
	return (
		<ConfirmModal
			controller={controller as AsyncModalController<unknown>}
			message={null}
			onResolve={handleOnSubmit}
			rejectLabel={t("general.just_save")}
			confirmButtonProps={{
				variant: "accent",
				icon: <Icon name="CheckCheck" />,
			}}
			confirmLabel={t("substitutions.finish_substitution_list")}
		>
			<FormSwitch
				control={methods.control}
				name="schedule"
			>
				{t("substitutions.send_notification_emails_on_a_later_date")}
			</FormSwitch>
			{schedule && (
				<DateInput
					label={t("dates.date")}
					isRequired
					control={methods.control}
					name="sendEmailsScheduleDate"
					minDate={moment()}
					maxDate={moment(startDate || "2100-01-01")}
				/>
			)}
		</ConfirmModal>
	);
}
